import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid, Button, Box, Divider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { format } from "date-fns";
import SearchBarAccount from "../Components/SearchBarAccount";
import { fromUTC } from "../GeneralUtil/timezoneUtil";
import SlidingSidebar from "../Components/SlidingSidebar";
import BookEvent from "./BookEvent";
import axios from "axios";
import { useUser } from "../UserContext";

interface BookedDate {
  date: string;
  slotsRemaining: number;
}

interface TimeSlot {
  slot_id: number;
  start_time: string;
  end_time: string;
  bookedDates: BookedDate[];
}

interface BA {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  avg_overall?: number;
}

interface BAAvailability {
  slot_id: number;
  start_time: string;
  end_time: string;
  available_bas: BA[];
}

interface SlotAvailability {
  [dayOfWeek: string]: TimeSlot[];
}

interface AccountAvailability {
  account_id: number;
  account: string;
  address: string;
  city: string;
  state_code: string;
  zip: string;
  avatar_url: string;
  available_slots: SlotAvailability;
  exclusion_dates: string[];
}

const SlotPicker: React.FC = () => {
  const { selectedBrandId } = useUser();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [selectedAccountId, setselectedAccountId] = useState<number | null>(
    null
  );
  const [baAvailability, setBaAvailability] = useState<BAAvailability[]>([]);
  const [isBAAvailable, setIsBAAvailable] = useState(false);
  const [availableBAs, setAvailableBAs] = useState<BA[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountAvailability, setAccountAvailability] =
    useState<AccountAvailability | null>(null);

  useEffect(() => {
    fetchAvailableSlots();
  }, [selectedAccountId]);

  const fetchAvailableSlots = async () => {
    if (!selectedAccountId) return;

    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/availability/available-slots/${selectedAccountId}?selectedBrandId=${selectedBrandId}`
      );

      // Find availability for selected account
      const accountData = response.data.find(
        (account: AccountAvailability) =>
          account.account_id === selectedAccountId
      );

      if (accountData) {
        // Sort available slots by start_time for each day of the week
        const sortedAvailableSlots = Object.keys(
          accountData.available_slots
        ).reduce((acc, day) => {
          acc[day] = accountData.available_slots[day].sort(
            (a: TimeSlot, b: TimeSlot) => {
              return a.start_time.localeCompare(b.start_time);
            }
          );
          return acc;
        }, {} as SlotAvailability);

        // Store the data exactly as it comes from the API
        setAccountAvailability({
          account_id: accountData.account_id,
          account: accountData.account, // Store account name
          address: accountData.address, // Store address
          city: accountData.city,
          state_code: accountData.state_code,
          zip: accountData.zip,
          avatar_url: accountData.avatar_url,
          available_slots: sortedAvailableSlots, // Set sorted available slots
          exclusion_dates: accountData.exclusion_dates || [],
        });
      }

      fetchBAAvailability();
    } catch (error) {
      console.error("Error fetching available slots:", error);
    }
  };

  const fetchBAAvailability = async () => {
    if (!selectedAccountId) return;
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/availability/ba-available-now/${selectedAccountId}`
      );

      setBaAvailability(response.data);
    } catch (error) {
      console.error("Error fetching BA availability:", error);
    }
  };

  // This function checks if a slot should be shown for the selected date
  const getAvailableSlotsForDate = (selectedDate: Dayjs | null) => {
    if (!selectedDate || !accountAvailability) return [];

    const dayOfWeek = selectedDate.format("dddd");
    const dateStr = selectedDate.format("YYYY-MM-DD");

    // Check if date is in exclusion dates
    if (accountAvailability.exclusion_dates.includes(dateStr)) {
      return [];
    }

    // Get slots for this day of week
    const daySlots = accountAvailability.available_slots[dayOfWeek] || [];

    // Filter slots based on availability
    return daySlots.filter((slot) => {
      // Check if this slot has any booked dates
      const bookedDate = slot.bookedDates?.find(
        (booked) => booked.date === dateStr
      );

      // If there's a booked date and no slots remaining, filter out this slot
      if (bookedDate && bookedDate.slotsRemaining <= 0) {
        return false;
      }

      return true;
    });
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
    setSelectedSlot(null);

    // Update available slots based on selected date
    const availableSlots = getAvailableSlotsForDate(newDate);
    setAvailableSlots(availableSlots);
  };

  const formatTime = (utcTime: string) => {
    const localTime = fromUTC(utcTime);
    return format(new Date(`2000-01-01T${localTime}`), "h:mm aa");
  };

  const handleSlotSelect = async (slotId: number) => {
    setSelectedSlot(selectedSlot === slotId ? null : slotId);

    const isBAAvailable = baAvailability.some((ba) => ba.slot_id === slotId);
    setIsBAAvailable(isBAAvailable);

    if (isBAAvailable) {
      try {
        const selectedBAResponse = await axios.get(
          `${
            import.meta.env.VITE_API_URL
          }/availability/ba-available-now/${selectedAccountId}`
        );
        const availableBAsForSlot =
          selectedBAResponse.data.find((ba: any) => ba.slot_id === slotId)
            ?.available_bas || [];

        const availableBAs = availableBAsForSlot.map((ba: any) => ({
          user_id: ba.ba_id,
          username: ba.username,
          first_name: ba.first_name,
          last_name: ba.last_name,
          avatar_url: ba.avatar_url,
          avg_overall: ba.overall_score,
        }));

        setAvailableBAs(availableBAs);
      } catch (error) {
        console.error("Error fetching available BAs:", error);
        setAvailableBAs([]);
      }
    } else {
      setAvailableBAs([]);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Book a Promotion
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "400px",
        }}
      >
        <Box sx={{ flex: 1, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SearchBarAccount
                boxSize="large"
                value={null}
                onSelectaccount={setselectedAccountId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={handleDateChange}
                  disablePast
                  sx={{
                    width: "100%",
                    "& .MuiPickersLayout-root": {
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "background.default",
                  height: "100%",
                  maxHeight: { xs: "300px", md: "400px" },
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="h1"
                  gutterBottom
                  sx={{
                    fontSize: ".8rem",
                    fontWeight: 400,
                    color: "text.primary",
                    mb: 3,
                  }}
                >
                  Available Time Slots
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {availableSlots.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: ".8rem",
                        fontWeight: 400,
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      No time slots available
                    </Typography>
                  ) : (
                    availableSlots.map((slot) => {
                      const isBAAvailable = baAvailability.some(
                        (ba) => ba.slot_id === slot.slot_id
                      );
                      return (
                        <Paper
                          key={slot.slot_id}
                          elevation={selectedSlot === slot.slot_id ? 3 : 1}
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            bgcolor:
                              selectedSlot === slot.slot_id
                                ? "primary.main"
                                : "background.paper",
                            color:
                              selectedSlot === slot.slot_id
                                ? "primary.contrastText"
                                : "text.primary",
                            "&:hover": {
                              bgcolor:
                                selectedSlot === slot.slot_id
                                  ? "primary.light"
                                  : "action.hover",
                            },
                          }}
                          onClick={() => handleSlotSelect(slot.slot_id)}
                        >
                          <Typography
                            sx={{
                              fontSize: ".8rem",
                              fontWeight: 400,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>
                              {formatTime(slot.start_time)} -{" "}
                              {formatTime(slot.end_time)}
                            </span>
                            {isBAAvailable && (
                              <Box
                                component="span"
                                sx={{
                                  border: "1px solid",
                                  borderRadius: "12px",
                                  padding: "2px 8px",
                                  borderColor: (theme) =>
                                    selectedSlot === slot.slot_id
                                      ? "white"
                                      : theme.palette.primary.main,
                                  color: (theme) =>
                                    selectedSlot === slot.slot_id
                                      ? "white"
                                      : theme.palette.primary.main,
                                  fontSize: "0.5rem",
                                }}
                              >
                                BOOK NOW
                              </Box>
                            )}
                          </Typography>
                        </Paper>
                      );
                    })
                  )}
                  {baAvailability.length === 0 && (
                    <Typography
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      No Brand Ambassadors available for this account.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedSlot}
              onClick={() => setSidebarOpen(true)}
              fullWidth
              size="large"
              sx={{ py: 1.5, fontSize: "1.1rem", fontWeight: 500 }}
            >
              Book
            </Button>
          </Box>
        </Box>
      </Box>

      <SlidingSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        width="600px"
      >
        {selectedSlot && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              pt: 1,
              pb: 6,
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <BookEvent
                selectedDate={selectedDate?.format("YYYY-MM-DD") || ""}
                selectedSlot={
                  availableSlots.find(
                    (slot) => slot.slot_id === selectedSlot
                  ) || null
                }
                accountId={selectedAccountId ?? 0}
                accountInfo={{
                  name: accountAvailability?.account || "",
                  address: `${accountAvailability?.address || ""}, ${
                    accountAvailability?.city || ""
                  }, ${accountAvailability?.state_code || ""} ${
                    accountAvailability?.zip || ""
                  }`,
                  avatar_url: accountAvailability?.avatar_url || "",
                }}
                isBAAvailable={isBAAvailable}
                availableBAs={availableBAs}
                onSuccess={fetchAvailableSlots}
                onClose={() => setSidebarOpen(false)}
              />
            </Box>
          </Box>
        )}
      </SlidingSidebar>
    </Paper>
  );
};

export default SlotPicker;
